import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

import SyncControls from '../components/sync/SyncControls';
import SyncProgress from '../components/sync/SyncProgress';
import LogViewer from '../components/sync/LogViewer';
import SyncSummary from '../components/sync/SyncSummary';
import SyncHistory from '../components/sync/SyncHistory';
import AdminNav from '../components/admin/AdminNav';

import '../styles/pages/SyncPage.scss';

const API_URL = process.env.REACT_APP_API_URL;

// El estado de la sync se lee por HTTP desde la BD: funciona aunque el navegador
// esté conectado a otro dyno que el que la ejecuta, y sobrevive a recargar la página.
const POLL_INTERVAL_MS = 2000;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admToken')}` });

const SyncPage = () => {
  const [run,            setRun]            = useState(null);
  const [logs,           setLogs]           = useState([]);
  const [lastByProvider, setLastByProvider] = useState({});
  const [error,          setError]          = useState('');
  const [starting,       setStarting]       = useState(null);
  const lastSeqRef = useRef(0);

  const isRunning = run?.status === 'running';

  // Estado general: última ejecución (con todos sus logs) y última sync de cada bodega.
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/sync/status`, { headers: authHeaders() });
      setLastByProvider(data.lastByProvider || {});
      setRun(data.run);
      setLogs(data.run?.logs || []);
      lastSeqRef.current = data.run?.lastSeq || 0;
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo leer el estado de la sincronización.');
    }
  }, []);

  // Se engancha a una ejecución concreta (recién lanzada, o ya en curso tras un 409).
  const followRun = useCallback((runId, provider) => {
    setRun({ id: runId, provider, status: 'running', current: 0, total: null, startedAt: new Date().toISOString() });
    setLogs([]);
    lastSeqRef.current = 0;
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Mientras hay una sync en curso, trae solo los cambios cada 2 s.
  useEffect(() => {
    if (!isRunning) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/admin/sync/status`, {
          headers: authHeaders(),
          params: { runId: run.id, after: lastSeqRef.current },
        });
        if (cancelled || !data.run) return;
        const { logs: newLogs, ...rest } = data.run;
        lastSeqRef.current = rest.lastSeq;
        if (newLogs.length) setLogs((prev) => [...prev, ...newLogs].slice(-300));
        setRun(rest);
        // Al terminar, refresca la "última sync" de cada bodega.
        if (rest.status !== 'running') loadStatus();
      } catch {
        // Un fallo puntual de red no corta el seguimiento; se reintenta en el siguiente ciclo.
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    poll();
    return () => { cancelled = true; clearInterval(timer); };
  }, [isRunning, run?.id, loadStatus]);

  const handleSync = async (provider) => {
    if (!localStorage.getItem('admToken')) { setError('Admin token not found. Please log in again.'); return; }

    setError('');
    setStarting(provider);
    try {
      const { data } = await axios.post(`${API_URL}/admin/sync/${provider}`, {}, { headers: authHeaders() });
      followRun(data.runId, provider);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.runId) {
        // Ya estaba corriendo (otra pestaña u otro usuario): se muestra su progreso.
        followRun(err.response.data.runId, provider);
      } else {
        setError(err.response?.data?.message || 'Failed to start sync process.');
      }
    } finally {
      setStarting(null);
    }
  };

  const syncingProvider = isRunning ? run.provider : starting;

  return (
    <div className="sync-page">
      <header className="sync-page__header">
        <h1>Tablero de sincronización</h1>
        <p className="sync-page__subtitle">Genius En‑Stock · Bodegas</p>
      </header>

      <AdminNav />

      {error && <div className="sync-page__alert">{error}</div>}

      <section className="sync-page__section">
        <SyncControls onSync={handleSync} syncingProvider={syncingProvider} lastByProvider={lastByProvider} />
      </section>

      {run && (
        <section className="sync-page__section">
          <SyncProgress run={run} lastLog={logs[logs.length - 1]} />
        </section>
      )}

      <section className="sync-page__section">
        <LogViewer logs={logs} isSyncing={isRunning} />
      </section>

      {run?.status === 'finished' && run.summary && (
        <section className="sync-page__section">
          <SyncSummary summary={run.summary} />
        </section>
      )}

      <section className="sync-page__section">
        {/* Se recarga al lanzar una sync y al terminar. */}
        <SyncHistory refreshKey={run ? `${run.id}-${run.status}` : ''} />
      </section>
    </div>
  );
};

export default SyncPage;
